import React from 'react';
import styled from '@emotion/styled';
import { image } from '@/theme';
import { Link } from 'react-router-dom';
import { FaxText, HotelReservationText, PhoneText } from '@/lib/react-intl/TranslatedTexts';
import { DangerouslyHtml } from '@/components/item';
import { IsDesktop, mq } from '@/utils/mediaQuery';
import { FOOTER_LOGO_ZINDEX } from '@/constants/zindex';
import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import { Image } from '..';

const Container = styled.div(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
  paddingBottom: '3rem',

  [mq('desktop')]: {
    gap: '0 6rem',
    flexDirection: 'row',
    paddingBottom: 0,
  },
}));

const FooterTopLeft = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '80%',
  margin: '0 auto',

  [mq('desktop')]: {
    gap: '4rem 0',
    maxWidth: '100%',
    margin: 0,
  },
}));

const FooterLogoWrapper = styled.div(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  zIndex: FOOTER_LOGO_ZINDEX,
  margin: '5rem 0',

  [mq('desktop')]: {
    padding: '4rem',
    height: '100%',
    margin: 0,
  },
}));

const Address = styled.address(({ theme }) => ({
  marginBottom: '1.8rem',
  fontStyle: 'normal',
  lineHeight: 1.2,
  fontWeight: theme.fontWeight.bold,

  '& p + p': {
    margin: 0,
  },
}));

const MenuWrapper = styled.div(() => ({
  marginBottom: '1rem',
  fontSize: '1.4rem',
  lineHeight: '1.5em',
}));

const MenuTitle = styled(Link)(({ theme }) => ({
  display: 'block',
  marginBottom: '3rem',
  fontWeight: theme.fontWeight.bold,
  fontSize: '1.8rem',
}));

const FooterLink = styled(Link)(({ theme, bold }) => ({
  fontWeight: bold === 'true' ? theme.fontWeight.bold : theme.fontWeight.regular,
}));

const FooterNav = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem 0',
  maxWidth: '20rem',
  margin: '0 auto',

  '& a:hover': {
    textDecoration: 'underline',
  },

  [mq('desktop')]: {
    margin: 0,
  },
}));

const FooterTopRight = styled.div(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '2rem 0',

  '& section': {
    width: '100%',
  },

  '& section.center': {
    marginTop: 0,
  },

  [mq('desktop')]: {
    padding: '4rem 0',
    justifyContent: 'space-between',
    flexDirection: 'row',
    textAlign: 'left',
    alignItems: 'flex-start',

    '& section.center': {
      marginTop: '5.4rem',
    },
  },
}));

const FooterHotelSection = styled.section(() => ({
  maxWidth: '36rem',
}));

const SectionHeader = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeight.bold,
  marginBottom: '3rem',
  lineHeight: 1.5,
  fontSize: '1.6rem',

  [mq('desktop')]: {
    fontSize: '1.8rem',
  },
}));

const ContactList = styled.ul(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem 0',
}));

const ContactItem = styled.li(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '0 1.4rem',
  flexWrap: 'wrap',
}));

const FooterTop = ({ footerAddress, footerMenu, footerContact }) => {
  const INSPIRE_HOMEPAGE_URL = 'https://www.inspireresorts.com';
  const lang = localStorage.getItem(LOCAL_LANGUAGE) || 'en';

  return (
    <Container>
      <FooterTopLeft>
        {footerAddress?.field_footerlogo && (
          <FooterLogoWrapper>
            <Image src={image.logoHeader} alt="logoHeader" width="auto" height="3rem" />
          </FooterLogoWrapper>
        )}
      </FooterTopLeft>

      <FooterTopRight>
        <section>
          {footerMenu?.length > 0 && (
            <MenuWrapper key={footerMenu?.[0]?.entity_id}>
              <MenuTitle to={`/${lang}${footerMenu?.[0]?.url}`}>
                <DangerouslyHtml value={footerMenu?.[0]?.title} />
              </MenuTitle>

              <FooterNav>
                {footerMenu?.[0]?.children.map((link) => {
                  return (
                    <FooterLink
                      to={`${INSPIRE_HOMEPAGE_URL}/${lang}${link.url}`}
                      key={link.entity_id}
                      target="_blank"
                    >
                      <DangerouslyHtml value={link.title} />
                    </FooterLink>
                  );
                })}
              </FooterNav>
            </MenuWrapper>
          )}
        </section>

        <section className="center">
          {footerMenu?.length > 0 && (
            <MenuWrapper key={footerMenu?.[1].entity_id}>
              <FooterNav>
                {footerMenu?.[1]?.children.map((link) => {
                  return (
                    <FooterLink
                      to={`${INSPIRE_HOMEPAGE_URL}/${lang}${link.url}`}
                      key={link.entity_id}
                      target="_blank"
                    >
                      <DangerouslyHtml value={link.title} />
                    </FooterLink>
                  );
                })}
              </FooterNav>
            </MenuWrapper>
          )}
        </section>

        <FooterHotelSection>
          <IsDesktop>
            <SectionHeader>
              {/*NOTE: Hotel Reservation*/}
              <HotelReservationText />
            </SectionHeader>
          </IsDesktop>

          <Address>
            <div>
              <DangerouslyHtml value={footerAddress?.field_address_line_1} />
            </div>

            <div>
              <DangerouslyHtml value={footerAddress?.field_address_line_2} />
            </div>
          </Address>

          <ContactList>
            <ContactItem>
              <span>
                {/*NOTE: phone*/}
                <PhoneText />
              </span>

              <a href={`tel:${footerContact?.field_phone_wd}`}>{footerContact?.field_phone_wd}</a>
            </ContactItem>

            <ContactItem>
              <span>
                {/*NOTE: FAX */}
                <FaxText />
              </span>

              <a href={`tel:${footerContact?.field_phone_wd}`}>{footerContact?.field_fax_wd}</a>
            </ContactItem>

            <a href={`mailto:${footerContact?.field_email_wd}`} style={{ marginTop: '2rem' }}>
              {footerContact?.field_email_wd}
            </a>
          </ContactList>
        </FooterHotelSection>
      </FooterTopRight>
    </Container>
  );
};

export default FooterTop;
